/**
 * Symbol display metadata: friendly names and logo URLs derived from a Symbol
 * record (or simple parsing of the ticker if metadata is missing).
 *
 * Public CDNs used (no auth required):
 *   - Flags:   https://flagcdn.com/h20/<iso2>.png
 *   - Crypto:  https://assets.coincap.io/assets/icons/<symbol>@2x.png
 */

const CURRENCY_ISO2 = {
  USD: 'us', EUR: 'eu', GBP: 'gb', JPY: 'jp', CHF: 'ch',
  AUD: 'au', CAD: 'ca', NZD: 'nz', CNY: 'cn', CNH: 'cn',
  HKD: 'hk', SGD: 'sg', SEK: 'se', NOK: 'no', DKK: 'dk',
  ZAR: 'za', MXN: 'mx', TRY: 'tr', RUB: 'ru', INR: 'in',
  BRL: 'br', PLN: 'pl', CZK: 'cz', HUF: 'hu', THB: 'th',
  KRW: 'kr', ILS: 'il', AED: 'ae', SAR: 'sa', TWD: 'tw',
}

const CURRENCY_NAMES = {
  USD: 'US Dollar',   EUR: 'Euro',          GBP: 'British Pound', JPY: 'Japanese Yen',
  CHF: 'Swiss Franc', AUD: 'Australian Dollar', CAD: 'Canadian Dollar',
  NZD: 'New Zealand Dollar', CNY: 'Chinese Yuan', CNH: 'Chinese Yuan',
  HKD: 'Hong Kong Dollar', SGD: 'Singapore Dollar', SEK: 'Swedish Krona',
  NOK: 'Norwegian Krone',  DKK: 'Danish Krone',    ZAR: 'South African Rand',
  MXN: 'Mexican Peso', TRY: 'Turkish Lira',   RUB: 'Russian Ruble',
  INR: 'Indian Rupee', BRL: 'Brazilian Real', PLN: 'Polish Zloty',
}

const CRYPTO_NAMES = {
  BTC: 'Bitcoin',  ETH: 'Ethereum', BNB: 'BNB',       SOL: 'Solana',
  XRP: 'XRP',      ADA: 'Cardano',  DOGE: 'Dogecoin', AVAX: 'Avalanche',
  DOT: 'Polkadot', MATIC: 'Polygon', LTC: 'Litecoin', LINK: 'Chainlink',
  TRX: 'TRON',     ATOM: 'Cosmos',  UNI: 'Uniswap',   ETC: 'Ethereum Classic',
  XLM: 'Stellar',  BCH: 'Bitcoin Cash', NEAR: 'Near Protocol',
  ALGO: 'Algorand', VET: 'VeChain', FIL: 'Filecoin',  AAVE: 'Aave',
  MKR: 'Maker',    SAND: 'The Sandbox', AXS: 'Axie Infinity',
  GRT: 'The Graph', SHIB: 'Shiba Inu', USDT: 'Tether',  USDC: 'USD Coin',
  TON: 'Toncoin',  TWT: 'Trust Wallet Token', WAVES: 'Waves',
}

/** Split a pair like "EUR/USD", "EURUSD", "BTC/USD:Binance" into {base, quote}. */
export function parsePair(raw) {
  if (!raw) return null
  let s = String(raw).split(':')[0] // drop exchange suffix
  if (s.includes('/')) {
    const [base, quote] = s.split('/')
    return { base: base.toUpperCase(), quote: quote.toUpperCase() }
  }
  // EURUSD form: split halves if length 6 and all letters
  if (/^[A-Z]{6}$/.test(s.toUpperCase())) {
    return { base: s.slice(0, 3).toUpperCase(), quote: s.slice(3, 6).toUpperCase() }
  }
  return null
}

function flagUrl(iso2) {
  if (!iso2) return null
  return `https://flagcdn.com/h40/${iso2}.png`
}

function cryptoUrl(symbol) {
  if (!symbol) return null
  return `https://assets.coincap.io/assets/icons/${symbol.toLowerCase()}@2x.png`
}

/** Free FMP logo CDN — no auth required for this specific endpoint. */
function stockLogoUrl(ticker) {
  if (!ticker) return null
  return `https://financialmodelingprep.com/image-stock/${ticker.toUpperCase()}.png`
}

/**
 * Best-effort ticker extraction:
 *   AAPL              -> AAPL
 *   AAPL:NASDAQ       -> AAPL   (matches graph.vue's TICKER:EXCHANGE convention)
 *   AAPL.US           -> AAPL
 *   NASDAQ:AAPL       -> NASDAQ (rare in this codebase; if you see it, set sourceName='AAPL')
 */
function stockTicker(s) {
  const raw = s.sourceName || s.id || s.name
  if (!raw) return null
  return String(raw).split(':')[0].split('.')[0].trim().toUpperCase()
}

/** True if the symbol is a forex pair we recognise. */
function isForex(s) {
  const pair = parsePair(s.sourceName || s.id || s.name)
  if (!pair) return false
  return CURRENCY_ISO2[pair.base] && CURRENCY_ISO2[pair.quote] || s.group?.toUpperCase().includes('FOREX')
}

/** True if the symbol is a stock pair we recognise. */
function isStock(s) {
  return s.group?.toUpperCase().includes('STOCK')
}

/** True if the symbol is a crypto pair we recognise. */
function isCrypto(s) {
  const pair = parsePair(s.sourceName || s.id || s.name)
  if (!pair) return false
  return CRYPTO_NAMES[pair.base] != null || s.group?.toUpperCase().includes('CRYPTO')
}

function typeOf(s) {
  const t = (s.type || '').toString().toUpperCase()
  if (t.includes('FOREX')   || t === 'FX')         return 'FX'
  if (t.includes('CRYPTO'))                        return 'CRYPTO'
  if (t.includes('STOCK')   || t === 'EQUITY')     return 'STOCK'
  if (t.includes('INDEX')   || t === 'INDICE')     return 'INDEX'
  if (t.includes('COMMOD')  || t === 'METAL')      return 'COMMODITY'
  // Fall back to ticker shape if type missing.
  if (isCrypto(s)) return 'CRYPTO'
  if (isForex(s))  return 'FX'
  if (isStock(s))  return 'STOCK'
  return 'OTHER'
}

/**
 * Returns up to 2 logos to display side-by-side, each {kind, src, alt}.
 * Falls back to a single initials avatar when nothing else matches.
 */
export function logosFor(s) {
  if (!s) return []
  const kind = typeOf(s)
  const raw  = s.sourceName || s.id || s.name

  if (kind === 'FX') {
    const pair = parsePair(raw)
    if (pair) {
      const a = flagUrl(CURRENCY_ISO2[pair.base])
      const b = flagUrl(CURRENCY_ISO2[pair.quote])
      if (a && b) return [{ kind: 'flag', src: a, alt: pair.base }, { kind: 'flag', src: b, alt: pair.quote }]
    }
  }

  if (kind === 'CRYPTO') {
    const pair = parsePair(raw)
    if (pair) {
      const a = cryptoUrl(pair.base)
      const b = CURRENCY_ISO2[pair.quote] ? flagUrl(CURRENCY_ISO2[pair.quote]) : cryptoUrl(pair.quote)
      const out = []
      if (a) out.push({ kind: 'crypto', src: a, alt: pair.base })
      if (b) out.push({ kind: b === a ? 'crypto' : 'flag', src: b, alt: pair.quote })
      if (out.length) return out
    }
  }

  // Manual override on the Symbol record takes precedence over derived URLs.
  if (s.logo && s.logo.trim() !== '') {
    return [{ kind: 'logo', src: s.logo, alt: s.name || raw }]
  }

  if (kind === 'STOCK') {
    const ticker = stockTicker(s)
    const url = stockLogoUrl(ticker)
    if (url) return [{ kind: 'stock', src: url, alt: ticker }]
  }

  // Last resort: text avatar handled by SymbolLogo component (no src).
  return []
}

/** Full descriptive name shown under the ticker. */
export function describe(s) {
  if (!s) return ''
  if (s.description && s.description.trim() !== '') return s.description.trim()

  const raw  = s.sourceName || s.id || s.name
  const kind = typeOf(s)

  if (kind === 'FX') {
    const pair = parsePair(raw)
    if (pair) {
      const a = CURRENCY_NAMES[pair.base]  || pair.base
      const b = CURRENCY_NAMES[pair.quote] || pair.quote
      return `${a} / ${b}`
    }
  }

  if (kind === 'CRYPTO') {
    const pair = parsePair(raw)
    if (pair) {
      const a = CRYPTO_NAMES[pair.base]    || pair.base
      const b = CURRENCY_NAMES[pair.quote] || CRYPTO_NAMES[pair.quote] || pair.quote
      return `${a} / ${b}`
    }
  }

  return s.name || ''
}

/** Up to 2 letters for the fallback avatar. */
export function initials(s) {
  if (!s) return '?'
  const src = (s.sourceName || s.id || s.name || '?').replace(/[^A-Z0-9]/gi, '')
  return (src.slice(0, 2) || '?').toUpperCase()
}
